/**
 * LINKED WALLETS COMPONENT
 * 
 * Displays all wallets linked to a user profile with:
 * - Verification status badges
 * - Verification timestamps
 * - Option to unlink wallets
 * - Truncated address display with copy functionality
 */

import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

// Helper to truncate wallet address
function truncateAddress(address, startChars = 4, endChars = 4) {
  if (!address || address.length <= startChars + endChars) return address;
  return `${address.slice(0, startChars)}...${address.slice(-endChars)}`;
}

// Helper to copy to clipboard
async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    console.error('Failed to copy:', err);
    return false;
  }
}

// Format date to readable string
function formatDate(dateString) {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

export default function LinkedWallets({ userId, isOwnProfile = false }) {
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedAddress, setCopiedAddress] = useState(null);

  // Load wallets for this user
  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    loadWallets();
  }, [userId]);

  const loadWallets = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from('user_wallets')
        .select('address, verified, verified_at, created_at, updated_at')
        .eq('user_id', userId)
        .order('verified', { ascending: false })
        .order('verified_at', { ascending: false, nullsFirst: false });

      if (fetchError) throw fetchError;

      setWallets(data || []);
    } catch (err) {
      console.error('Error loading wallets:', err);
      setError('Failed to load linked wallets');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (address) => {
    const success = await copyToClipboard(address);
    if (success) {
      setCopiedAddress(address);
      setTimeout(() => setCopiedAddress(null), 2000);
    }
  };

  const handleUnlink = async (address) => {
    if (!confirm(`Are you sure you want to unlink wallet ${truncateAddress(address)}?`)) {
      return;
    }

    try {
      // Note: In production, you might want to handle this via Edge Function
      // For now, we'll set verified to false (soft unlink)
      const { error: unlinkError } = await supabase
        .from('user_wallets')
        .update({ verified: false })
        .eq('address', address)
        .eq('user_id', userId);

      if (unlinkError) throw unlinkError;

      // Reload wallets
      await loadWallets();
    } catch (err) {
      console.error('Error unlinking wallet:', err);
      console.error('Failed to unlink wallet: ' + err.message); // Note: Add notification system if parent needs it
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded-lg">
        {error}
      </div>
    );
  }

  if (wallets.length === 0) {
    return (
      <div className="text-gray-400 text-center py-8">
        No wallets linked yet
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white">
          Linked Wallets ({wallets.length})
        </h3>
      </div>

      <div className="space-y-2">
        {wallets.map((wallet) => (
          <div
            key={wallet.address}
            className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 flex items-center justify-between"
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <button
                  onClick={() => handleCopy(wallet.address)}
                  className="font-mono text-sm text-gray-300 hover:text-white transition-colors"
                  title="Click to copy full address"
                >
                  {truncateAddress(wallet.address, 6, 6)}
                </button>
                
                {copiedAddress === wallet.address && (
                  <span className="text-xs text-green-400">Copied!</span>
                )}

                {wallet.verified ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-900/50 text-green-400 border border-green-500">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Verified
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-700 text-gray-400 border border-gray-600">
                    Unverified
                  </span>
                )}
              </div>

              <div className="text-xs text-gray-500">
                {wallet.verified ? (
                  <>Verified: {formatDate(wallet.verified_at)}</>
                ) : (
                  <>Added: {formatDate(wallet.created_at)}</>
                )}
              </div>
            </div>

            {isOwnProfile && wallet.verified && (
              <button
                onClick={() => handleUnlink(wallet.address)}
                className="ml-4 px-3 py-1.5 text-sm rounded-lg border border-red-500/50 text-red-400 hover:bg-red-900/30 transition-colors"
              >
                Unlink
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

